/* @flow */
import * as React from 'react';
import invariant from 'invariant';
import idx from 'idx';

import assertions from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Countries, { regionsForCountry } from 'lib/geo/Country';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import OrganizationActions from 'actions/OrganizationActions';
import Row from 'components/Row';
import Select from 'components/Select';
import ToastActions from 'actions/ToastActions';

import type { ValidOrgCountry } from 'lib/geo/Country';
import type {
  OrganizationType,
  createOrganizationUnauthed,
  CreateOrganizationUnauthedInput,
} from 'organizations/queries/__generated__/createOrganizationUnauthed';

type Props = {
  createOrgWhileUnauthed: (
    data: CreateOrganizationUnauthedInput
  ) => Promise<{ data: createOrganizationUnauthed }>,
  defaultOrganization?: string,
  onCreate: (*) => void,
  onRequestHide: () => void,
  organizationType?: OrganizationType,
};

type State = {
  name: ?string,
  city: ?string,
  region: ?string,
  type: OrganizationType,
  country: ValidOrgCountry,
  submitting: boolean,
};

class OrganizationCreateForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const userCountry = CurrentUser.isSignedIn()
      ? CurrentUser.getUser().getCountryCode()
      : undefined;
    const country = userCountry === 'US' || userCountry === 'CA' ? userCountry : Countries[0].value;

    this.state = {
      name: props.defaultOrganization,
      city: undefined,
      region: undefined,
      country,
      type: props.organizationType || 'School',
      submitting: false,
    };
  }

  disableButton = () => {
    this.setState({ submitting: true });
  };

  enableButton = () => {
    this.setState({ submitting: false });
  };

  requiredFieldsAreFilled(fields: State) {
    return fields.name && fields.city && fields.region && fields.country && fields.type;
  }

  handleCityChange = (city: ?string) => {
    this.setState({ city });
  };

  handleCountryChange = (country: ValidOrgCountry) => {
    this.setState({ country });
    this.handleRegionChange(undefined);
  };

  handleRegionChange = (region: ?string) => {
    this.setState({ region });
  };

  handleNameChange = (name: ?string) => {
    this.setState({ name });
  };

  handleOrgTypeChange = (type: OrganizationType) => {
    this.setState({ type });
  };

  submitAuthed = () => {
    this.disableButton();

    if (!this.requiredFieldsAreFilled(this.state)) {
      console.warn({ fields: this.state });
      console.error('OrganizationCreateForm - tried to submitAuthed with missing field');
      ToastActions.showErrorToast('Oops! Looks like you’re missing a field.');
      return;
    }

    const { name, city, country, region, type } = this.state;

    OrganizationActions.createOrganization({
      name,
      city,
      country,
      state: region,
      type,
    }).then(organization => {
      this.props.onRequestHide();
      this.props.onCreate(organization.toJSON());
    }, ToastActions.showAPIError);
  };

  submitUnauthed = () => {
    this.disableButton();
    const clientUuid = EventTracking.getClientId();

    if (!clientUuid) {
      console.error(
        'OrganizationCreateForm cannot get clientUuid because window is type undefined'
      );
      ToastActions.showErrorToast('Oops! Something went wrong.');
      this.enableButton();
      return;
    }

    const { name, city, region, type, country } = this.state;

    if (!this.requiredFieldsAreFilled(this.state)) {
      console.warn({ fields: this.state });
      console.error('OrganizationCreateForm - tried to submitAuthed with missing field');
      ToastActions.showErrorToast('Oops! Looks like you’re missing a field.');
      return;
    }

    this.props
      .createOrgWhileUnauthed({
        // $FlowFixMe: flow is dumb and doesn't know that we're checking these
        name,
        // $FlowFixMe
        city,
        country,
        // $FlowFixMe
        state: region,
        type,
        clientUuid,
      })
      .then(
        ({ data }) => {
          const { error } = data.createOrganizationUnauthed;
          if (error) {
            errorLogger('OrganizationCreateForm error with unauthed org creation')(error);

            ToastActions.showErrorToast(
              idx(error, _ => _.fieldErrors[0].message) || 'Oops! Something went wrong.'
            );
            this.enableButton();
            return;
          }

          invariant(this.props.onCreate, 'onCreate is falsey in OrganizationCreateForm');
          this.props.onCreate(data.createOrganizationUnauthed.organization);
          this.props.onRequestHide();
        },
        e => {
          errorLogger('OrganizationCreateForm error with unauthed org creation')(e);
          ToastActions.showErrorToast('Oops! Something went wrong.');
          this.enableButton();
        }
      );
  };

  render() {
    const { organizationType } = this.props;
    const { submitting, region, type, country } = this.state;
    const canSubmit = this.requiredFieldsAreFilled(this.state) && !submitting;

    const regionLabel = regionsForCountry[country].label;

    return (
      <BoxBody divided>
        <Row size="small">
          {!organizationType && (
            <FormElement
              label="Organization type"
              onValidChange={value => this.handleOrgTypeChange(value)}
              validate={value => {
                assertions.notEmpty(value, 'Please select an organization type');
              }}
              element={Select}
              elementProps={{
                name: 'type',
                value: type,
                options: ['School', 'District'],
              }}
              data-test="type-field"
            />
          )}

          <FormElement
            label={`${type} name`}
            onValidChange={e => this.handleNameChange(e.target.value)}
            onInvalidChange={() => this.handleNameChange(undefined)}
            validate={e => {
              assertions.notEmpty(e.target.value, `Your ${type.toLowerCase()} name is required.`);
            }}
            element={Input}
            elementProps={{
              defaultValue: this.props.defaultOrganization,
              name: 'name',
            }}
            data-test="name-field"
          />

          <FormElement
            label="Country"
            onValidChange={value => this.handleCountryChange(value)}
            validate={value => {
              assertions.notEmpty(value, 'Please select a country');
            }}
            element={Select}
            elementProps={{
              name: 'country',
              value: country,
              options: Countries,
            }}
            data-test="country-field"
          />

          <FormElement
            label="City"
            onValidChange={e => this.handleCityChange(e.target.value)}
            onInvalidChange={() => this.handleCityChange(undefined)}
            validate={e => {
              assertions.notEmpty(e.target.value, 'Your city is required.');
            }}
            element={Input}
            elementProps={{
              name: 'city',
            }}
            data-test="city-field"
          />

          <FormElement
            label={regionLabel}
            onValidChange={value => this.handleRegionChange(value)}
            onInvalidChange={() => this.handleRegionChange(undefined)}
            validate={value => {
              assertions.notEmpty(value, `Please select a ${regionLabel.toLowerCase()}`);
            }}
            element={Select}
            elementProps={{
              name: 'region',
              value: region,
              options: regionsForCountry[country].regions,
            }}
            data-test="region-field"
          />
        </Row>
        <Button
          data-test="submit-button"
          onClick={CurrentUser.isSignedIn() ? this.submitAuthed : this.submitUnauthed}
          disabled={!canSubmit}
          eventElementName="OrganizationCreateForm-submit">
          Save
        </Button>
      </BoxBody>
    );
  }
}

export default OrganizationCreateForm;
